import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { HomeIcon, SearchIcon, ArrowRight } from 'lucide-react';
interface NotFoundPageProps {
  onNavigate: (page: string) => void;
}
export function NotFoundPage({
  onNavigate
}: NotFoundPageProps) {
  // Generate random stars for the background
  const stars = useMemo(() => {
    return Array.from({
      length: 40
    }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 3 + 2,
      delay: Math.random() * 2
    }));
  }, []);
  return <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 to-indigo-950 relative overflow-hidden flex flex-col items-center justify-center px-4 py-12" data-id="element-26327">
      {/* Animated Starfield Background */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-26328">
        {stars.map(star => <motion.div key={star.id} className="absolute bg-white rounded-full" style={{
        left: `${star.x}%`,
        top: `${star.y}%`,
        width: star.size,
        height: star.size,
        opacity: 0.2
      }} animate={{
        opacity: [0.2, 0.8, 0.2],
        scale: [1, 1.2, 1]
      }} transition={{
        duration: star.duration,
        delay: star.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-26329" />)}
      </div>

      {/* Decorative Glows */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 z-0" data-id="element-26330" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 z-0" data-id="element-26331" />

      {/* Main Content */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="relative z-10 text-center max-w-2xl mx-auto w-full" data-id="element-26332">
        {/* Animated Planet/Moon and 404 */}
        <div className="relative flex justify-center items-center mb-8 h-48 sm:h-64" data-id="element-26333">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 120,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute w-40 h-40 sm:w-56 sm:h-56 rounded-full bg-gradient-to-br from-indigo-400 to-purple-900 shadow-[0_0_60px_rgba(99,102,241,0.3)] opacity-80" data-id="element-26334">
            <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-60 mix-blend-multiply" data-id="element-26335" />
            <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-26336" />
          </motion.div>

          <motion.div initial={{
          scale: 0.8
        }} animate={{
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut',
          delay: 0.2
        }} className="relative z-10 text-7xl sm:text-9xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white to-indigo-300 drop-shadow-2xl select-none" data-id="element-26337">
            404
          </motion.div>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-26338">
          Страница затерялась в космосе
        </h1>

        <p className="text-lg sm:text-xl text-indigo-200 mb-10 max-w-lg mx-auto leading-relaxed" data-id="element-26339">
          Возможно, эта страница переместилась в другую галактику. Давайте
          вернём вас на орбиту.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16" data-id="element-26340">
          <button onClick={() => onNavigate('home')} className="inline-flex items-center justify-center px-8 py-3.5 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl active:scale-95 text-base" data-id="element-26341">
            <HomeIcon className="w-5 h-5 mr-2" data-id="element-26342" />
            На главную
          </button>
          <button onClick={() => onNavigate('marketplace')} className="inline-flex items-center justify-center px-8 py-3.5 border border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all active:scale-95 text-base backdrop-blur-sm" data-id="element-26343">
            <SearchIcon className="w-5 h-5 mr-2" data-id="element-26344" />
            Каталог услуг
          </button>
        </div>

        {/* Quick Links */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} className="pt-8 border-t border-white/10" data-id="element-26345">
          <p className="text-sm text-indigo-300/70 mb-4 uppercase tracking-wider font-medium" data-id="element-26346">
            Популярные направления
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-26347">
            {[{
            label: 'Натальная карта',
            page: 'service-natal'
          }, {
            label: 'Астрологи',
            page: 'astrologers'
          }, {
            label: 'Астро-календарь',
            page: 'astro-daily'
          }, {
            label: 'Блог',
            page: 'blog'
          }].map(link => <button key={link.page} onClick={() => onNavigate(link.page)} className="group flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-indigo-200 bg-white/5 rounded-full hover:bg-white/10 hover:text-white transition-all border border-white/5 hover:border-white/20" data-id="element-26348">
                {link.label}
                <ArrowRight className="w-3.5 h-3.5 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" data-id="element-26349" />
              </button>)}
          </div>
        </motion.div>
      </motion.div>
    </div>;
}